import React, { useEffect, useState } from "react";
import axiosAuth from "../Axios/Interceptors";
import AppContext from "../Context/Context";
import { useGlobalState } from "../Context/GlobalStateContext";
import moment from "moment";
import { NumbersInWords } from "../Shared/Utility";
import PackingJob from "./PackingJob";
import PTDragDropUpload from "../DragDrop/PTDragDropUpload";

// Props interface
interface ServiceInfoProps {
  quoteId: number;
  totalServices?: number;
  serviceNumber?: number;
  isVanLevel?: boolean;
}

const Floors = [
  "Ground Floor",
  "1st Floor",
  "2nd  Floor",
  "3rd  Floor",
  "4th  Floor",
  "5th  Floor",
  "6th  Floor",
  "7th Floor",
  "8th Floor",
  "9th Floor",
  "10th Floor",
  "11th Floor",
  "12th Floor",
  "13th Floor",
  "14th Floor",
  "15th Floor",
  "16th Floor",
  "17th Floor",
  "18th Floor",
  "19th Floor",
  "20th Floor",
  "20th Floor+",
];

const currencyFormat = (value: number | null | undefined) => {
  if (value === null || value === undefined) return "£0.00";
  // keep two decimals
  return `£${value?.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const ServiceInfo: React.FC<ServiceInfoProps> = ({
  quoteId,
  totalServices = 1,
  serviceNumber = 1,
  isVanLevel = false,
}) => {
  const globalState = useGlobalState();

  // state copied from Angular
  let [quoteInfo, setQuoteInfo] = useState<any>({});
  const [MNVQuoteAdditionalStops, setMNVQuoteAdditionalStops] = useState<any[]>(
    [],
  );
  const [ServiceQuestionAnswers, setServiceQuestionAnswers] = useState<any[]>(
    [],
  );
  const [AdditionalServices, setAdditionalServices] = useState<any[]>([]);
  const [AdditionalRateAndVAT, setAdditionalRateAndVAT] = useState<number>(0);
  const [MediaObjectID, setMediaObjectID] = useState<number>(quoteId);
  const [LineItems, setLineItems] = useState<any[]>([]);
  const [LineMainItems, setLineMainItems] = useState<any[]>([]);
  const [LineItemsAmounts, setLineItemsAmounts] = useState<any>({
    Credit: 0,
    Debit: 0,
    Total: 0,
    Paid: 0,
    Refund: 0,
    Balance: 0,
    Liability: 0,
    LiabilityID: 0,
    PendingLineItemCount: 0,
    OtherDiscount: 0,
    OverAllOtherDiscount: 0,
    Cancellation: 0,
    OverallCancellation: 0,
    OverAllCredit: 0,
    OverAllDebit: 0,
    OverAllRefund: 0,
    OverAllSubtotal: 0,
    OverAllVAT: 0,
    OverAllTotal: 0,
    OverAllPaid: 0,
    OverAllBalance: 0,
  });

  // update media object ID when quoteId changes (like ctor)
  useEffect(() => {
    setMediaObjectID(quoteId);
  }, [quoteId]);

  useEffect(() => {
    if (quoteId && quoteId > 0) {
      GetQuoteInfoById();
    }
  }, [quoteId]);

  // GetQuoteInfoById
  const GetQuoteInfoById = async () => {
    const data = { MNVQuoteID: quoteId };
    try {
      const response = await axiosAuth.post(
        AppContext.APIURL + "api/MNVQuote/MNVQuoteGetQuoteByID",
        data,
      );
      const result = response.data.Data;

      // Angular expects result[0][0] and result[1] etc
      if (Array.isArray(result) && result.length > 0) {
        const qInfo = (result[0] && result[0][0]) || {};
        quoteInfo = qInfo;
        setQuoteInfo(qInfo);
        if (qInfo.ParentID > 0) setMediaObjectID(qInfo.ParentID);
        else setMediaObjectID(quoteId);

        setMNVQuoteAdditionalStops(result[1] || []);
        if (!isVanLevel) setAdditionalServices(result[4] || []);
        // after setting quote info, get service questions and amounts
        GetServiceQuestionAndAnswers(quoteId);
        CalculateAdditionalServicesAmounts(qInfo, result[4] || []);
      } else {
        console.error("Unexpected GetQuoteInfoById result:", result);
      }
    } catch (error) {
      console.error("GetQuoteInfoById error:", error);
    }
  };

  // CalculateAdditionalServicesAmounts
  const CalculateAdditionalServicesAmounts = (
    qInfoParam?: any,
    additional?: any[],
  ) => {
    const qInfo = qInfoParam || quoteInfo;
    const addServices = additional || AdditionalServices;

    let l_intLutonVans = 0;
    let l_intTransitVans = 0;
    let l_intNumberOfMovers = qInfo?.MNVMoverTypeID || 0;

    if (qInfo?.MNVVanTypeID == 1) l_intTransitVans = 1;
    else l_intLutonVans = 1;

    for (let i = 0; i < (addServices?.length || 0); i++) {
      const s = addServices[i];
      if (s.MNVVanTypeID == 1) l_intTransitVans += 1;
      else l_intLutonVans += 1;
      l_intNumberOfMovers += s.MNVMoverTypeID || 0;
    }

    let multiVanText = "";
    if (l_intLutonVans > 0 && l_intTransitVans > 0)
      multiVanText =
        (NumbersInWords?.[l_intLutonVans] || l_intLutonVans) +
        " Luton & " +
        (NumbersInWords?.[l_intTransitVans] || l_intTransitVans) +
        " Transit";
    else if (l_intLutonVans > 0)
      multiVanText =
        (NumbersInWords?.[l_intLutonVans] || l_intLutonVans) + " Luton";
    else if (l_intTransitVans > 0)
      multiVanText =
        (NumbersInWords?.[l_intTransitVans] || l_intTransitVans) + " Transit";

    const multiMoverText =
      (NumbersInWords?.[l_intNumberOfMovers] || l_intNumberOfMovers) +
      " Movers";

    setQuoteInfo((prev: any) => ({
      ...prev,
      MultiVanText: multiVanText,
      MultiMoverText: multiMoverText,
    }));
  };

  // GetServiceQuestionAndAnswers
  const GetServiceQuestionAndAnswers = async (quoteIdParam: number) => {
    try {
      const data = { QuoteID: quoteIdParam };
      const response = await axiosAuth.post(
        AppContext.APIURL + "api/MNVQuote/MNVGetServiceOptionsResponseV1",
        data,
      );
      const result = response.data;

      setServiceQuestionAnswers((result && result[0]) || []);
      if (!globalState.IsDriverLoggedIn && !globalState.IsHelperLoggedIn) {
        GetAdditionalCharges();
      }
    } catch (error) {
      console.error("GetServiceQuestionAndAnswers error:", error);
    }
  };

  // GetAdditionalCharges
  const GetAdditionalCharges = async () => {
    try {
      const data: any = { MNVQuoteID: quoteId };
      const response = await axiosAuth.post(
        AppContext.APIURL +
          "api/MNVRates/MNVRatesGetDataSetForAdditionalCharges",
        data,
      );

      const result = response.data.Data;
      const Rates = result && result[0] ? result[0] : [];
      const Tier = "Tier" + (quoteInfo?.TierID || "") + "Add";
      const VanTierRate =
        Rates.filter(
          (z: any) =>
            z.ObjectTypeID == 1 && z.ObjectID == quoteInfo?.MNVVanTypeID,
        )[0] || {};
      const DriverTierRate =
        Rates.filter((z: any) => z.ObjectTypeID == 2 && z.ObjectID == 0)[0] ||
        {};
      const VanAdditionalRate = (VanTierRate && VanTierRate[Tier]) || 0;
      const DriverAdditionalRate =
        (DriverTierRate && DriverTierRate[Tier]) || 0;

      let newAdditionalRateAndVAT = 0;
      if (quoteInfo?.MNVMoverTypeID == 1) {
        newAdditionalRateAndVAT = VanAdditionalRate;
      } else if (quoteInfo?.MNVMoverTypeID == 2) {
        newAdditionalRateAndVAT = VanAdditionalRate + DriverAdditionalRate;
      } else if (quoteInfo?.MNVMoverTypeID == 3) {
        newAdditionalRateAndVAT =
          VanAdditionalRate + DriverAdditionalRate + DriverAdditionalRate;
      }
      setAdditionalRateAndVAT(newAdditionalRateAndVAT);
    } catch (error) {
      console.error("GetAdditionalCharges error:", error);
    }
  };

  // GetInvoiceDataByQuoteID
  const GetInvoiceDataByQuoteID = async () => {
    try {
      // Angular uses QuoteInfo.MNVJobID as MNVQuoteID
      const data: any = { MNVQuoteID: quoteInfo?.MNVJobID };
      const response = await axiosAuth.post(
        AppContext.APIURL + "api/MNVQuote/GetInvoiceDataByQuoteID",
        data,
      );
      const result = response.data.Data;
      const l_arrResponseInvoice = (result && result[0]) || [];
      const l_arrResponseLineItems = (result && result[1]) || [];
      setLineMainItems((result && result[2]) || []);
      const l_arrLineItems: any[] = [];

      // reset amounts
      const newAmounts: any = {
        Credit: 0,
        Debit: 0,
        Refund: 0,
        Cancellation: 0,
        OverallCancellation: 0,
        OverAllCredit: 0,
        OverAllDebit: 0,
        OverAllRefund: 0,
        OverAllSubtotal: 0,
        OverAllVAT: 0,
        OverAllTotal: 0,
        OverAllPaid: 0,
        OverAllBalance: 0,
        OverAllOtherDiscount: 0,
      };

      if (l_arrResponseInvoice.length > 0) {
        newAmounts.Balance = l_arrResponseInvoice[0].Balance;
      }

      let l_decPaidViaThirdParty = 0;
      let l_decOverAllPaidViaBankCashEtc = 0;
      let l_decPaidViaBankCashEtc = 0;
      let l_decOtherDiscountWriteOff = 0;
      let l_decOverAllOtherDiscountWriteOff = 0;

      for (let i = 0; i < l_arrResponseLineItems.length; i++) {
        const item = l_arrResponseLineItems[i];

        if (item.Status == 1) {
          newAmounts.PendingLineItemCount =
            (newAmounts.PendingLineItemCount || 0) + 1;
        }

        if (item.SourceType == 2 || item.SourceType == 3) {
          if (item.TransactionType == 1) {
            newAmounts.OverAllCredit += item.Amount;
          } else {
            newAmounts.OverAllDebit += item.Amount;
          }

          if (item.OtherObjectID == quoteId) {
            l_arrLineItems.push(item);
            if (item.TransactionType == 1) newAmounts.Credit += item.Amount;
            else newAmounts.Debit += item.Amount;
          } else {
            for (let as = 0; as < AdditionalServices.length; as++) {
              if (
                item.OtherObjectID == AdditionalServices[as].MNVQuoteID ||
                item.OtherObjectID == AdditionalServices[as].ParentID
              ) {
                l_arrLineItems.push(item);
                if (item.TransactionType == 1) newAmounts.Credit += item.Amount;
                else newAmounts.Debit += item.Amount;
              }
            }
          }
        }

        if (
          item.TransactionType == 2 &&
          item.SourceType != 4 &&
          item.ObjectType == 2
        ) {
          // PaidViaThirdParty
          l_decPaidViaThirdParty += item.Amount;
        }

        if (
          item.TransactionType == 2 &&
          item.SourceType != 4 &&
          item.ObjectType == 3
        ) {
          // manual paid
          l_decOverAllPaidViaBankCashEtc += item.Amount;

          if (item.OtherObjectID == quoteId) {
            l_decPaidViaBankCashEtc += item.Amount;
          } else {
            for (let as = 0; as < AdditionalServices.length; as++) {
              if (
                item.OtherObjectID == AdditionalServices[as].MNVQuoteID ||
                item.OtherObjectID == AdditionalServices[as].ParentID
              ) {
                l_decPaidViaBankCashEtc += item.Amount;
              }
            }
          }
        }

        if (
          item.TransactionType == 2 &&
          item.SourceType != 4 &&
          item.ObjectType == 4
        ) {
          // Other debit.. adjustment, other discount etc..
          l_decOverAllOtherDiscountWriteOff += item.Amount;

          if (item.OtherObjectID == quoteId) {
            l_decOtherDiscountWriteOff += item.Amount;
          } else {
            for (let as = 0; as < AdditionalServices.length; as++) {
              if (
                item.OtherObjectID == AdditionalServices[as].MNVQuoteID ||
                item.OtherObjectID == AdditionalServices[as].ParentID
              ) {
                l_decOtherDiscountWriteOff += item.Amount;
              }
            }
          }
        }

        if (
          item.TransactionType == 2 &&
          item.SourceType != 4 &&
          item.ObjectType == 5
        ) {
          // cancellation
          newAmounts.OverallCancellation += item.Amount;

          if (item.OtherObjectID == quoteId)
            newAmounts.Cancellation += item.Amount;
          else {
            for (let as = 0; as < AdditionalServices.length; as++) {
              if (
                item.OtherObjectID == AdditionalServices[as].MNVQuoteID ||
                item.OtherObjectID == AdditionalServices[as].ParentID
              ) {
                newAmounts.Cancellation += item.Amount;
              }
            }
          }
        }

        if (item.TransactionType == 3) {
          newAmounts.Refund += item.Amount;
          newAmounts.OverAllRefund += item.Amount;
        }
      }

      setLineItems(l_arrLineItems);
      newAmounts.Total = newAmounts.Credit;
      newAmounts.OverAllTotal = newAmounts.OverAllCredit;
      newAmounts.Paid = l_decPaidViaBankCashEtc + l_decPaidViaThirdParty;
      newAmounts.OverAllPaid =
        l_decOverAllPaidViaBankCashEtc + l_decPaidViaThirdParty;
      newAmounts.OtherDiscount = l_decOtherDiscountWriteOff;
      newAmounts.OverAllOtherDiscount = l_decOverAllOtherDiscountWriteOff;

      setLineItemsAmounts(newAmounts);
    } catch (error) {
      console.error("GetInvoiceDataByQuoteID error:", error);
    }
  };

  // call GetInvoiceDataByQuoteID once quoteInfo has job id and user is not driver/helper
  useEffect(() => {
    if (
      quoteInfo &&
      quoteInfo.MNVJobID &&
      !globalState.IsDriverLoggedIn &&
      !globalState.IsHelperLoggedIn
    ) {
      GetInvoiceDataByQuoteID();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteInfo, AdditionalServices]);

  function ResponseFromPTUploader(data: any) {}

  function getStairName(stairID: any) {
    let name = "N/A";
    for (let i = 0; i < AppContext.stairsList.length; i++) {
      if (AppContext.stairsList[i].PID == stairID)
        name = AppContext.stairsList[i].Name;
    }
    return name;
  }

  // Render
  return (
    <div id="Step5" className="grid-d mt-20">
      <span className="font-16 text-left label-color bold-font font-mont mt-15 flex-d">
        Order Detail
      </span>

      <div className="grid-d fr-31 flex-gap20" id="detail">
        <div className="grid-d">
          <div className="grid-d fr-111 gap-20 mt-20 flex-start font-mont info-grid">
            <div className="grid-d flex-gap10">
              <span className="font-16 text-left label-color bold-font">
                Contact Name
              </span>
              <span className="font-12 text-left label-color">
                {quoteInfo?.ClientName}
              </span>
              <span className="font-12 text-left label-color">
                {quoteInfo?.Mobile}
                <a
                  href={`https://wa.me/${quoteInfo?.Mobile?.replace("-", "")}`}
                  target="_blank"
                  rel="noreferrer"
                  title="Open in WhatsApp"
                  className="flex-d"
                >
                  <img
                    src="/images/whatsapp.svg"
                    width="20px"
                    height="20px"
                    alt="Edit"
                  />
                </a>
              </span>
            </div>

            <div className="grid-d flex-gap5">
              <span className="font-16 text-left label-color bold-font">
                Pickup Address
              </span>
              <span className="font-12 text-left label-color">
                {quoteInfo?.FromPostalCode}
              </span>
              <span className="font-12 text-left label-color">
                {quoteInfo?.FromAddress}
              </span>
              <span className="font-12 text-left label-color">
                {quoteInfo?.FromCity}
              </span>

              <div className="flex-da flex-gap3">
                <span className="font-12 text-left c-theme">
                  {quoteInfo?.FromType}
                </span>
                <span className="font-12 text-left c-theme">|</span>
                {quoteInfo?.FromNumberOfStairs > 0 && (
                  <span className="font-12 text-left c-theme">
                    {Floors[quoteInfo.FromNumberOfStairs]}
                  </span>
                )}
                {quoteInfo?.FromNumberOfStairs == -1 && (
                  <span className="font-12 text-left c-theme">
                    Ground Floor
                  </span>
                )}
                {quoteInfo?.FromNumberOfStairs == -2 && (
                  <span className="font-12 text-left c-theme">Basement</span>
                )}
                <span className="font-12 text-left c-theme">|</span>
                {quoteInfo?.FromAvailableLift == "No" && (
                  <span className="font-12 text-left c-theme">
                    {quoteInfo.FromAvailableLift} Lift
                  </span>
                )}
                {quoteInfo?.FromAvailableLift == "Yes" && (
                  <span className="font-12 text-left c-theme">
                    Lift Available
                  </span>
                )}
                {quoteInfo?.FromAvailableLift == "N/A" && (
                  <span className="font-12 text-left c-theme">N/A</span>
                )}
                <span className="font-12 text-left c-theme">|</span>
                <span className="font-12 text-left c-theme">
                  {getStairName(quoteInfo?.FromAdditionalStairs)}
                </span>
              </div>
            </div>

            <div className="grid-d flex-gap5">
              {quoteInfo?.MNVMoveTypeServiceID != 7 && (
                <>
                  <span className="font-16 text-left label-color bold-font">
                    Delivery Address
                  </span>
                  <span className="font-12 text-left label-color">
                    {quoteInfo?.ToPostalCode}
                  </span>
                  <span className="font-12 text-left label-color">
                    {quoteInfo?.ToAddress}
                  </span>
                  <span className="font-12 text-left label-color">
                    {quoteInfo?.ToCity}
                  </span>
                  <div className="flex-da flex-gap5">
                    <span className="font-12 text-left c-theme">
                      {quoteInfo?.ToType}
                    </span>
                    <span className="font-12 text-left c-theme">|</span>
                    {quoteInfo?.ToNoOfStairs > 0 && (
                      <span className="font-12 text-left c-theme">
                        {Floors[quoteInfo.ToNoOfStairs]}
                      </span>
                    )}
                    {quoteInfo?.ToNoOfStairs == -1 && (
                      <span className="font-12 text-left c-theme">
                        Ground Floor
                      </span>
                    )}
                    {quoteInfo?.ToNoOfStairs == -2 && (
                      <span className="font-12 text-left c-theme">
                        Basement
                      </span>
                    )}
                    <span className="font-12 text-left c-theme">|</span>
                    {quoteInfo?.ToAvailableLift == "No" && (
                      <span className="font-12 text-left c-theme">
                        {quoteInfo.ToAvailableLift} Lift
                      </span>
                    )}
                    {quoteInfo?.ToAvailableLift == "Yes" && (
                      <span className="font-12 text-left c-theme">
                        Lift Available
                      </span>
                    )}
                    {quoteInfo?.ToAvailableLift == "N/A" && (
                      <span className="font-12 text-left c-theme">N/A</span>
                    )}
                    <span className="font-12 text-left c-theme">|</span>
                    <span className="font-12 text-left c-theme">
                      {getStairName(quoteInfo?.ToAdditionalStairs)}
                    </span>
                  </div>
                </>
              )}
            </div>

            <div className="grid-d flex-gap5">
              <span className="font-16 text-left label-color bold-font">
                Time & Date
              </span>
              <span className="font-12 text-left label-color">
                {quoteInfo?.RequestedDate
                  ? moment(quoteInfo.RequestedDate).format("LL")
                  : ""}
              </span>
              <span className="font-12 text-left label-color">
                {quoteInfo?.RequestedTime}
              </span>
            </div>

            <div className="grid-d flex-gap5">
              <span className="font-16 text-left label-color bold-font">
                Distance
              </span>
              <span className="font-12 text-left label-color">
                {quoteInfo?.PTDDistance != null
                  ? `${quoteInfo.PTDDistance?.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })} miles`
                  : ""}
              </span>
            </div>

            <div className="grid-d flex-gap5">
              <span className="font-16 text-left label-color bold-font">
                Extra Stops
              </span>
              <span className="font-12 text-left label-color">
                {MNVQuoteAdditionalStops.length === 0 ? "No" : "Yes"}
              </span>
            </div>
          </div>

          {MNVQuoteAdditionalStops && MNVQuoteAdditionalStops.length > 0 && (
            <div className="grid-d flex-gap5 mt-20">
              <span className="font-16 text-left label-color bold-font">
                Additional Stops
              </span>

              {MNVQuoteAdditionalStops.map((stop: any, index: number) => (
                <div className="grid-d flex-gap5" key={index}>
                  <span className="font-12 text-left label-color">
                    Stop {index + 1}:
                  </span>
                  <span className="font-12 text-left label-color">
                    {stop.PostCode}
                  </span>
                  <span className="font-12 text-left label-color">
                    {stop.Address}
                  </span>
                  <span className="font-12 text-left label-color">
                    {stop.City}
                  </span>
                  <div className="flex-da flex-gap5">
                    <span className="font-12 text-left c-theme">
                      {stop.AdditionalStopType}
                    </span>
                    <span className="font-12 text-left c-theme">|</span>
                    {stop.NoOfStairs > 0 && (
                      <span className="font-12 text-left c-theme">
                        {Floors[stop.NoOfStairs]}
                      </span>
                    )}
                    {stop.NoOfStairs == -1 && (
                      <span className="font-12 text-left c-theme">
                        Ground Floor
                      </span>
                    )}
                    {stop.NoOfStairs == -2 && (
                      <span className="font-12 text-left c-theme">
                        Basement
                      </span>
                    )}
                    <span className="font-12 text-left c-theme">|</span>
                    {stop.AdditionalStopAvailableLift == "No" && (
                      <span className="font-12 text-left c-theme">
                        {stop.AdditionalStopAvailableLift} Lift
                      </span>
                    )}
                    {stop.AdditionalStopAvailableLift == "Yes" && (
                      <span className="font-12 text-left c-theme">
                        Lift Available
                      </span>
                    )}
                    {stop.AdditionalStopAvailableLift == "N/A" && (
                      <span className="font-12 text-left c-theme">N/A</span>
                    )}
                    <span className="font-12 text-left c-theme">|</span>
                    <span className="font-12 text-left c-theme">
                      {getStairName(stop?.WaitingTimeInSeconds)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="grid-d fr-111 gap-20 mt-20 flex-start info-grid">
            {AdditionalServices.length == 0 ? (
              <>
                {quoteInfo?.MNVMoveTypeServiceID != 7 && (
                  <div className="grid-d flex-gap5">
                    <span className="font-16 text-left label-color bold-font">
                      Van Size
                    </span>
                    {quoteInfo?.MNVVanTypeID == 1 && (
                      <span className="grid-d flex-gap5">
                        <img
                          style={{ height: 70 }}
                          src="/images/transit.svg"
                          alt="transit"
                        />

                        <span className="font-12 text-left label-color">
                          Transit
                        </span>
                      </span>
                    )}
                    {quoteInfo?.MNVVanTypeID == 2 && (
                      <span className="grid-d flex-gap5">
                        <img
                          style={{ height: 70 }}
                          src="/images/luton.svg"
                          alt="luton"
                        />

                        <span className="font-12 text-left label-color">
                          Luton
                        </span>
                      </span>
                    )}
                  </div>
                )}

                {quoteInfo?.MNVMoveTypeServiceID == 7 && (
                  <div className="grid-d flex-gap5">
                    <span className="font-16 text-left label-color bold-font">
                      Van Size
                    </span>
                    <span className="grid-d flex-gap5">
                      <img
                        style={{ height: 70 }}
                        src="/images/packing.svg"
                        alt="packing"
                      />
                      <span className="font-12 text-left label-color">
                        Packing Service
                      </span>
                    </span>
                  </div>
                )}
              </>
            ) : (
              <div className="grid-d flex-gap5">
                <span className="font-16 text-left label-color bold-font">
                  Van
                </span>
                <span className="font-12 text-left label-color">
                  {quoteInfo?.MultiVanText}
                </span>
              </div>
            )}

            {AdditionalServices.length == 0 ? (
              <div className="grid-d flex-gap5">
                <span className="font-16 text-left label-color bold-font">
                  Movers
                </span>
                {quoteInfo?.MNVMoverTypeID == 1 && (
                  <img
                    alt="img"
                    src="/images/onemover.png"
                    style={{ height: 70 }}
                  />
                )}
                {quoteInfo?.MNVMoverTypeID == 2 && (
                  <img
                    alt="img"
                    src="/images/twomovers.png"
                    style={{ height: 70 }}
                  />
                )}
                {quoteInfo?.MNVMoverTypeID == 3 && (
                  <img
                    alt="img"
                    src="/images/threemovers.png"
                    style={{ height: 70 }}
                  />
                )}

                {quoteInfo?.MNVMoveTypeServiceID != 7 && (
                  <>
                    {quoteInfo?.MNVMoverTypeID == 1 && (
                      <span className="font-12 text-left label-color">
                        One Mover
                      </span>
                    )}
                    {quoteInfo?.MNVMoverTypeID == 2 && (
                      <span className="font-12 text-left label-color">
                        Two Mover
                      </span>
                    )}
                    {quoteInfo?.MNVMoverTypeID == 3 && (
                      <span className="font-12 text-left label-color">
                        Three Mover
                      </span>
                    )}
                  </>
                )}
                {quoteInfo?.MNVMoveTypeServiceID == 7 && (
                  <>
                    {quoteInfo?.MNVMoverTypeID == 1 && (
                      <span className="font-12 text-left label-color">
                        One Packer
                      </span>
                    )}
                    {quoteInfo?.MNVMoverTypeID == 2 && (
                      <span className="font-12 text-left label-color">
                        Two Packers
                      </span>
                    )}
                    {quoteInfo?.MNVMoverTypeID == 3 && (
                      <span className="font-12 text-left label-color">
                        Three Packers
                      </span>
                    )}
                  </>
                )}
              </div>
            ) : (
              <div className="grid-d flex-gap5">
                <span className="font-16 text-left label-color bold-font">
                  Movers
                </span>
                <span className="font-12 text-left label-color">
                  {quoteInfo?.MultiMoverText}
                </span>
              </div>
            )}

            <div className="grid-d flex-gap5">
              {quoteInfo?.IsLocalQuote === true ? (
                <>
                  <span className="font-16 text-left label-color bold-font">
                    Service Time
                  </span>
                  <div className="field-input">
                    <select
                      disabled
                      className="input-field w-100"
                      value={quoteInfo?.RequiredVanTime}
                      onChange={() => {}}
                    >
                      {[
                        1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8,
                        8.5, 9, 9.5, 10, 10.5, 11, 11.5, 12, 12.5, 13, 13.5, 14,
                        14.5, 15,
                      ].map((v) => (
                        <option key={v} value={v}>
                          {v % 1 === 0 ? `${v} hours` : `${v} hours`}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              ) : (
                <div className="grid-d flex-gap10">
                  <span className="font-16 text-left label-color bold-font">
                    Time
                  </span>
                  <div className="grid-d flex-gap3">
                    <span className="font-12 text-left label-color">
                      Loading Time
                    </span>
                    <div className="field-input">
                      <select
                        disabled
                        className="input-field"
                        value={quoteInfo?.LoadingTime}
                        onChange={() => {}}
                      >
                        {[
                          15, 30, 45, 60, 90, 120, 150, 180, 210, 240, 270, 300,
                        ].map((v) => (
                          <option key={v} value={v}>
                            {v === 60 ? "1hr" : `${v}mins`}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="grid-d flex-gap3">
                    <span className="font-12 text-left label-color">
                      Unloading Time
                    </span>
                    <div className="field-input">
                      <select
                        disabled
                        className="input-field"
                        value={quoteInfo?.UnloadingTime}
                        onChange={() => {}}
                      >
                        {[
                          15, 30, 45, 60, 90, 120, 150, 180, 210, 240, 270, 300,
                        ].map((v) => (
                          <option key={v} value={v}>
                            {v === 60 ? "1hr" : `${v}mins`}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="flow-auto mt-25 auto-height">
            <table className="reuse-table">
              <thead>
                <tr>
                  <th colSpan={2}>{quoteInfo?.ServiceName}</th>
                </tr>
              </thead>
              <tbody>
                {ServiceQuestionAnswers.map((response, idx) => (
                  <tr key={idx}>
                    <td>{response.Questions}</td>
                    <td>{response.Answers}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PackingJob QuoteID={quoteId} IsReadOnly={true} />

          {quoteInfo?.FromFloor && quoteInfo.FromFloor !== "" && (
            <div className="grid-d flex-gap10 mt-20">
              <span className="font-16 text-left label-color bold-font">
                Driver Instructions
              </span>
              <span
                className="font-12 text-left label-color"
                dangerouslySetInnerHTML={{
                  __html: quoteInfo.FromFloor,
                }}
              ></span>
            </div>
          )}
          <div className="grid-d flex-gap10 mt-20">
            <span className="font-16 text-left label-color bold-font">
              Comments & Extra Info
            </span>
            <span className="font-12 text-left label-color">
              {quoteInfo?.ClientInstructions}
            </span>
          </div>
          <div className="grid-d flex-gap10 mt-20">
            <PTDragDropUpload
              ObjectType={1}
              ObjectID={MediaObjectID}
              UploadType={1}
              MaxNumberOfFiles={0}
              CanEditFiles={false}
              onResponse={ResponseFromPTUploader}
            />
          </div>
        </div>

        {/* Right column - totals */}
        {!globalState.IsDriverLoggedIn && !globalState.IsHelperLoggedIn && (
          <>
            <div className="box-mov-cont">
              <div className="flex-d font-16bold-font label-color">
                {totalServices === 1
                  ? "Order Total"
                  : `Service ${serviceNumber} Total`}
              </div>
              <div className="flex-da flex-jsb ser-prc mt-10">
                <span className="font-12 text-left label-color bold-font">
                  Services
                </span>
                <span className="font-12 text-left label-color bold-font">
                  Price
                </span>
              </div>
              <div className="flex-d flex-jse">
                <div className="grid-d flex-gap10 w-100">
                  <div className="flex-d flex-jsb flex-gap5 total-bor-reuse2">
                    <span className="c-theme font-13 bold-font">
                      Quote Number: {quoteInfo?.JobID}
                    </span>
                    <span className="c-theme font-13 bold-font">
                      {quoteInfo?.TotalAmount != null
                        ? `£${quoteInfo.TotalAmount?.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}`
                        : ""}
                    </span>
                  </div>

                  {quoteInfo?.Discount != 0 && (
                    <>
                      <div className="flex-d flex-jse flex-gap5">
                        <span className="c-theme font-13 bold-font">
                          Discount: -{" "}
                          {quoteInfo?.Discount != null
                            ? `£${quoteInfo.Discount?.toLocaleString(
                                undefined,
                                {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                },
                              )}`
                            : ""}
                        </span>
                      </div>
                    </>
                  )}
                  {LineItems.map((LineItem, idx) => (
                    <div className="flex-d flex-jse flex-gap5" key={idx}>
                      {LineItem.TransactionType == 1 && (
                        <span
                          className="c-theme font-13 bold-font"
                          title={LineItem.Notes}
                        >
                          {LineItem.RefferenceNumber}:
                          {LineItem.TransactionType == 1
                            ? ` ${currencyFormat(LineItem.Amount)}`
                            : ` -${currencyFormat(LineItem.Amount)}`}
                        </span>
                      )}
                    </div>
                  ))}

                  <div className="flex-d flex-jse flex-gap5">
                    <span className="c-theme font-13 bold-font">
                      Subtotal:{" "}
                      {quoteInfo?.Charges != null
                        ? `£${quoteInfo.Charges?.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}`
                        : "£0.00"}
                    </span>
                  </div>

                  <div className="flex-d flex-jse flex-gap5">
                    <span className="c-theme font-13 bold-font">
                      VAT:{" "}
                      {quoteInfo?.Vat != null
                        ? `£${quoteInfo.Vat?.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}`
                        : "£0.00"}
                    </span>
                  </div>

                  <div className="flex-d flex-jse flex-gap5 total-bor-reuse">
                    <span className="c-theme font-13 bold-font">
                      Total:{" "}
                      {quoteInfo?.PayableAmount != null
                        ? `£${quoteInfo.PayableAmount?.toLocaleString(
                            undefined,
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            },
                          )}`
                        : "£0.00"}
                    </span>
                  </div>
                </div>
              </div>
              <div className="grid-d mt-10 font-12 label-color text-left">
                * Any additional time will be charged at{" "}
                {AdditionalRateAndVAT != null
                  ? `£${AdditionalRateAndVAT?.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}`
                  : "£0.00"}{" "}
                + VAT per 30mins. Please keep additional time up to 1hr maximum.
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ServiceInfo;
